import { useAtom } from "jotai";
import { useEffect, useState } from "react";
import { STORE_POG_NOTES_MAX_LENGTH } from "@CommonApi/merchflow/store/pog/_storePogId_/notes";
import { useModals } from "src/components/Modals";
import { Button, Color, Flex, Modal, Spinner, Text, Textarea } from "src/elements";
import {
  useSaveStorePogNotesMutation,
  useStorePogNotesQuery,
} from "src/api/merchflows/storePogNotes";
import { useTranslation } from "src/i18n";
import { dateIsoStringToLocaleString } from "src/utils";
import { ConfirmModalProps } from "../ModalConfirm/store/types";
import { storePogNotesModalAtom, type StorePogNotesModalData } from "./store/atoms";

export const StorePogNotesModal = () => {
  const [data, setData] = useAtom(storePogNotesModalAtom);

  useEffect(() => {
    return () => {
      setData(null);
    };
  }, []);

  return data ? <StorePogNotesModalInner {...data} /> : null;
};

const StorePogNotesModalInner = ({ storePogId, canEdit }: StorePogNotesModalData) => {
  const { closeModal: _closeModal, openConfirmModal } = useModals();
  const { data: saved, isLoading, isError } = useStorePogNotesQuery(storePogId);
  const saveNotes = useSaveStorePogNotesMutation();
  const { t } = useTranslation("common");

  const [draft, setDraft] = useState<string | null>(null);
  const notes = draft ?? saved?.notes ?? "";
  const isModified = draft !== null && draft !== saved?.notes;
  const isTooLong = notes.length > STORE_POG_NOTES_MAX_LENGTH;

  const closeModal = () => {
    _closeModal("StorePogNotesModal");
  };

  const closeModalSafe = () => {
    if (isModified) {
      openConfirmModal(getConfirmationModalProps());
    } else {
      closeModal();
    }
  };

  const save = async () => {
    try {
      await saveNotes.mutateAsync({ storePogId, payload: { notes } });
      closeModal();
    } catch {
      return;
    }
  };

  const getConfirmationModalProps = () => {
    return {
      title: t("storePogNotes.discardTitle"),
      description: t("storePogNotes.discardDescription"),
      buttons: [
        {
          id: "continue-editing",
          label: t("storePogNotes.continueEditing"),
          color: "primary",
          variant: "inverted",
        },
        {
          id: "discard-changes",
          label: t("storePogNotes.discardChanges"),
          color: "red",
          variant: "default",
          onClick: closeModal,
        },
      ],
    } satisfies ConfirmModalProps;
  };

  const getLastEditedLabel = () => {
    if (!saved?.updatedAt) return t("storePogNotes.noNotes");
    const date = dateIsoStringToLocaleString(saved.updatedAt);
    return saved.updatedByName
      ? t("storePogNotes.lastEditedBy", { name: saved.updatedByName, date })
      : t("storePogNotes.lastEdited", { date });
  };

  return (
    <Modal
      name="StorePogNotesModal"
      title={t("storePogNotes.title", { id: storePogId })}
      width="500px"
      confirmModal={isModified && getConfirmationModalProps()}
    >
      <Flex column padding="10px" gap="10px">
        {isLoading && (
          <Flex justify="center" padding="20px">
            <Spinner />
          </Flex>
        )}

        {isError && <Text color={Color.red}>{t("storePogNotes.loadFailed")}</Text>}

        {saved && (
          <>
            <Textarea
              title={t("storePogNotes.field")}
              value={notes}
              setValue={canEdit && !saveNotes.isPending ? setDraft : undefined}
              height="200px"
            />

            <Flex justify="between" gap="10px">
              <Text variant="small2" color={Color.textSecondary}>
                {getLastEditedLabel()}
              </Text>

              {canEdit && (
                <Text variant="small2" color={isTooLong ? Color.red : Color.textSecondary}>
                  {notes.length}/{STORE_POG_NOTES_MAX_LENGTH}
                </Text>
              )}
            </Flex>
          </>
        )}

        <Flex gap="10px">
          <Button
            color={isModified ? "red" : "primary"}
            variant="inverted"
            width={canEdit && saved ? "50%" : "100%"}
            onClick={closeModalSafe}
          >
            {isModified ? t("cancel") : t("back")}
          </Button>

          {canEdit && saved && (
            <Button
              width="50%"
              onClick={save}
              isEnabled={isModified && !isTooLong}
              isLoading={saveNotes.isPending}
            >
              {t("save")}
            </Button>
          )}
        </Flex>
      </Flex>
    </Modal>
  );
};
